
import axios, { AxiosResponse } from 'axios';
import { CodingRefinementRequest, IQueryResponseWithId, IDiscoverTextProject, IDiscoverTextProjectArchives, InitialQueryDescriptionRequest, IRunningTasks, ISampledPostItemCollection, ISampleQueryRound, ISavedQueries, IXSifterCreateSavedQueryResponse, IXSifterEstimateResponse, IXSifterQuery, IXSifterQueryRoot, IXSifterSavedQuery, IXSifterSavedQueryListRequest, QueryRefinementResult, QueryRefinementResultList, SubmitQueryResponse } from '../../types/XSifterQueries';
import { IDTResourceCollection, tryGetErrorMessage } from '../../types/XSifterGeneralTypes';
import { RuleGroupType } from 'react-querybuilder';

export const XSIFTER_PROJECTS_URL = process.env.XSIFTER_BACKEND + "/projects";
export const XSIFTER_PROJECT_ARCHIVES_URL = process.env.XSIFTER_BACKEND + "/project/archives";
export const XSIFTER_CREATE_SAVED_QUERY_URL = process.env.XSIFTER_BACKEND + "/queries";
export const XSIFTER_GET_SAVED_QUERY_URL = process.env.XSIFTER_BACKEND + "/queries";
export const XSIFTER_QUERY_ESTIMATE_URL = process.env.XSIFTER_BACKEND + "/query/estimate";
export const XSIFTER_QUERY_SUBMIT_URL = process.env.XSIFTER_BACKEND + "/query/submit";
export const XSIFTER_GET_RUNNING_TASKS_URL = process.env.XSIFTER_BACKEND + "/jobs";
export const XSIFTER_GET_SAVED_QUERIES_URL = process.env.XSIFTER_BACKEND + "/queries";
export const XSIFTER_USER_INITIAL_QUERY_URL = process.env.XSIFTER_BACKEND + "/query/initial";
export const XSIFTER_TRANSLATE_TWITTER_QUERY_URL = process.env.XSIFTER_BACKEND + "/query/translate";
export const XSIFTER_GET_CREATE_OR_NEXT_CODING_ROUND_URL = process.env.XSIFTER_BACKEND + "/query/sample/round";
export const XSIFTER_GET_SAMPLE_POSTS_URL = process.env.XSIFTER_BACKEND + "/query/sample/fetch";
export const XSIFTER_FETCH_SAMPLE_STREAM_URL = process.env.XSIFTER_BACKEND + "/query/sample/fetchposts";
export const XSIFTER_SCORE_SAMPLED_ITEM_URL = process.env.XSIFTER_BACKEND + "/query/sample/score";
export const XSIFTER_PERFORM_REFINE_QUERY_URL = process.env.XSIFTER_BACKEND + "/query/refine";
export const XSIFTER_UPDATE_QUERY_FROM_REFINEMENT_URL = process.env.XSIFTER_BACKEND + "/query/applyrefinement";

export const QUERY_STRUCT_VERSION: number = 2;

const QUERY_ID_RESPONSE_HEADER = "x-xsifter-queryid";

// the query ID is chosen by the client and sent on the request; the server may or may not echo it back,
// so fall back to the ID we sent rather than reporting null (which would wipe the ID held in state).
function resolveQueryId(response: AxiosResponse, sentQueryId: string | null): string | null {
    return response.headers?.[QUERY_ID_RESPONSE_HEADER] ?? sentQueryId;
}

function withQueryId<T extends object>(response: AxiosResponse<T>, sentQueryId: string | null): T & IQueryResponseWithId {
    return { ...response.data, currentQueryId: resolveQueryId(response, sentQueryId) };
}

export default class ApiQueryService {
    async getEstimate(token: string, queryTraceId: string | null, query: IXSifterQueryRoot): Promise<IXSifterEstimateResponse> {
        return new Promise<IXSifterEstimateResponse>((resolve, reject) => {
            axios.post(XSIFTER_QUERY_ESTIMATE_URL, query, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            })
            .then((response: AxiosResponse) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async getDiscoverTextProjects(token: string): Promise<IDTResourceCollection<IDiscoverTextProject>> {
        return new Promise<IDTResourceCollection<IDiscoverTextProject>>((resolve, reject) => {
            let params = {
                limit: 1000,
                sort: "name:asc"
            };

            axios.get(XSIFTER_PROJECTS_URL, {
                params: params,
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            })
            .then((response: AxiosResponse) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async getDiscoverTextArchivesForProject(token: string, projectId: number): Promise<IDiscoverTextProjectArchives> {
        return new Promise<IDTResourceCollection<IDiscoverTextProject>>((resolve, reject) => {
            let params = {
                limit: 1000,
                sort: "name:asc"
            };

            let url = `${XSIFTER_PROJECT_ARCHIVES_URL}/${projectId}`;

            axios.get(url, {
                params: params,
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            })
            .then((response: AxiosResponse) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async saveNewQuery(token: string, queryTraceId: string | null, query: IXSifterQuery, queryTitle: string, estimateCount: number): Promise<IXSifterCreateSavedQueryResponse> {
        let postData = {
            title: queryTitle,
            query: {...query, version: QUERY_STRUCT_VERSION },
            estimateCount: estimateCount
        };

        return new Promise<IXSifterCreateSavedQueryResponse>((resolve, reject) => {
            axios.post(XSIFTER_CREATE_SAVED_QUERY_URL, postData, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            }).then((response: AxiosResponse) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async updateSavedQuery(token: string, queryTraceId: string | null, savedQueryId: string, query: IXSifterQuery, queryTitle: string, estimateCount: number): Promise<IXSifterCreateSavedQueryResponse> {
        let postData = {
            savedQueryId: savedQueryId,
            title: queryTitle,
            query: {...query, version: QUERY_STRUCT_VERSION },
            estimateCount: estimateCount
        };

        return new Promise<IXSifterCreateSavedQueryResponse>((resolve, reject) => {
            let url = `${XSIFTER_CREATE_SAVED_QUERY_URL}/${savedQueryId}`;
            axios.put(url, postData, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            }).then((response: AxiosResponse) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async submitFetch(token: string, queryTraceId: string | null, savedQueryId: string, projectId: number, archiveName?: string, existingArchiveId?: number): Promise<SubmitQueryResponse> {
        let postData = {
            savedQueryId: savedQueryId,
            projectId: projectId,
            newArchiveName: archiveName,
            existingArchiveId: existingArchiveId
        };
        return new Promise<SubmitQueryResponse>((resolve, reject) => {
            axios.post(XSIFTER_QUERY_SUBMIT_URL, postData, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            }).then((response: AxiosResponse) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async getRunningTasks(token: string) : Promise<IRunningTasks> {
        return new Promise<IRunningTasks>((resolve, reject) => {
            axios.get(XSIFTER_GET_RUNNING_TASKS_URL, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async getSavedQueries(token: string, request?: IXSifterSavedQueryListRequest) : Promise<ISavedQueries> {
        return new Promise<ISavedQueries>((resolve, reject) => {
            let params = { offset: request?.offset ?? 0, limit:  request?.limit ?? 10 };
            if (request?.query)
                params["title"] = request.query;
            if (request?.sortField)
                params["sort"] = `${request.sortField}:${request.sortDirection}`;
            if (request?.submitted)
                params["submitted"] = true;

            axios.get(XSIFTER_GET_SAVED_QUERIES_URL, {
                params: params,
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async getSavedQuery(token: string, queryId: string): Promise<IXSifterSavedQuery | null> {
        return new Promise<IXSifterSavedQuery | null>((resolve, reject) => {
            let url = `${XSIFTER_GET_SAVED_QUERY_URL}/${queryId}`;
            axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async cancelRunningJob(token: string, taskId: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            let url = `${XSIFTER_GET_RUNNING_TASKS_URL}/${taskId}`;
            axios.delete(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                resolve(true);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        })
    }

    async deleteSavedQuery(token: string, queryId: string): Promise<IXSifterSavedQuery> {
        return new Promise<IXSifterSavedQuery>((resolve, reject) => {
            let url = `${XSIFTER_GET_SAVED_QUERY_URL}/${queryId}`;
            axios.delete(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async translateTwitterQuery(token: string, twitterQuery: string): Promise<RuleGroupType | null | string> {
        return new Promise<RuleGroupType | null | string>((resolve, reject) => {
            axios.get(XSIFTER_TRANSLATE_TWITTER_QUERY_URL, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                params: { 'query': twitterQuery }
            }).then((response) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async translateSavedQuery(token: string, queryId: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            let url = `${XSIFTER_GET_SAVED_QUERIES_URL}/${queryId}/translate`;
            axios.get(url,{
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                resolve(response.data);
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async getCodingRound(token: string, queryTraceId: string | null, codingRoundId: string): Promise<ISampleQueryRound> {
        return new Promise<ISampleQueryRound>((resolve, reject) => {
            let url = `${XSIFTER_GET_CREATE_OR_NEXT_CODING_ROUND_URL}/${codingRoundId}`;
            axios.get(url,{
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            }).then((response: AxiosResponse) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async getOrCreateCodingRound(token: string, queryTraceId: string | null, savedQueryId: string): Promise<ISampleQueryRound> {
        return new Promise<ISampleQueryRound>((resolve, reject) => {
            let url = `${XSIFTER_GET_CREATE_OR_NEXT_CODING_ROUND_URL}/${savedQueryId}`;
            axios.post(url, null, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            }).then((response: AxiosResponse) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        });
    }

    async doFetchSampleQueryItems(token: string, queryTraceId: string | null, codingRoundId: string, abortController: AbortController | null) {
        let url = `${XSIFTER_FETCH_SAMPLE_STREAM_URL}/${codingRoundId}`;
        return fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`, 
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Cache-Control': 'no-cache',
                'X-XSifter-QueryId': queryTraceId ?? ""
            },
            signal: abortController?.signal
        });
    }

    async getSampledItemsForCoding(token: string, codingRoundId: string): Promise<ISampledPostItemCollection> {
        return new Promise<ISampledPostItemCollection>((resolve, reject) => {
            let url = `${XSIFTER_GET_SAMPLE_POSTS_URL}/${codingRoundId}`;            
            return axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                if (response.status === 200) {
                    let responseData: ISampledPostItemCollection = response.data;
                    responseData.items = responseData.items.slice(0, 20);
                    responseData.meta.count = responseData.items.length;
                    responseData.meta.limit = responseData.items.length;
                    resolve(responseData);
                } else
                    reject(response.data.error ?? "An unknown error occured.");
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        })
    }

    async scoreSampledItem(token: string, codingRoundId: string, codedSampleId: string, itemWeight: number): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            let url = `${XSIFTER_SCORE_SAMPLED_ITEM_URL}/${codingRoundId}/${codedSampleId}`;
            let queryParams = {
                'score': itemWeight
            };

            return axios.post(url, null, {
                params: queryParams,
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            }).then((response: AxiosResponse) => {
                if (response.status === 200)
                    resolve(true);
                else
                    reject(response.data.error ?? "An unknown error occured.");
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        })
    }

    async getInitialQueryFromDescription(token: string, queryTraceId: string | null, request: InitialQueryDescriptionRequest): Promise<QueryRefinementResultList> {
        return new Promise<QueryRefinementResultList>((resolve, reject) => {
            axios.post(XSIFTER_USER_INITIAL_QUERY_URL, request, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            })
            .then((response) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        })
    }

    async submitForRefinement(token: string, queryTraceId: string | null, request: CodingRefinementRequest): Promise<QueryRefinementResultList> {
        return new Promise<QueryRefinementResultList>((resolve, reject) => {
            axios.post(XSIFTER_PERFORM_REFINE_QUERY_URL, request, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            })
            .then((response) => {
                resolve(withQueryId(response, queryTraceId));
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        })
    }

    async applyQueryRefinement(token: string, queryTraceId: string | null, savedQueryId: string, refinement: QueryRefinementResult): Promise<IQueryResponseWithId> {
        return new Promise<IQueryResponseWithId>((resolve, reject) => {
            let url = `${XSIFTER_UPDATE_QUERY_FROM_REFINEMENT_URL}/${savedQueryId}`;
            axios.post(url, {
                query: refinement.query
            }, {
                headers: { 
                    'Authorization': `Bearer ${token}`, 
                    'Content-Type': 'application/json',
                    'X-XSifter-QueryId': queryTraceId
                }
            })
            .then((response) => {
                resolve({ currentQueryId: resolveQueryId(response, queryTraceId) });
            }).catch((error: any) => {
                reject(tryGetErrorMessage(error));
            });
        })
    }
}